import MainLayout from 'Components/Layout/MainLayout';
import {Button, message, Space, Spin} from 'antd';
import React, {useRef} from 'react';
import Style from './index.module.scss';
import TableRender, {ProColumnsType, TableContext} from 'table-render';
import useTable from '../../../Utils/Hooks/useTable';
import {deleteAccount, getAccountPage, AccountList} from './service';
import SelectDict from '../../../Features/Select/SelectDict';
import useRequestFn from '../../../Utils/Hooks/useRequestFn';
import {errorMessage, formatDate} from '../../../Utils/common';
import DeleteConfirm from '../../../Components/DeleteConfirm';
import useModalHandler from '../../../Utils/Hooks/useModalHandler';
import EditModal from './Components/EditModal';
import {PlusOutlined} from '@ant-design/icons';
import {useDict} from './store';
import OverText from '../../../Components/OverText';
import AccountImportModal from './Components/AccountImportModal';

export default function Index() {
    const dict = useDict();
    const {tableInit} = useTable(getAccountPage, {pageSize: 20});
    const tableRef = useRef<TableContext>(null);
    const editModal = useModalHandler({
        component: EditModal,
    });
    const accountImportModal = useModalHandler({
        component: AccountImportModal,
    });

    function reload() {
        tableRef?.current?.refresh();
    }

    const toolBar = (
        <div className={Style.toolBar}>
            <Space>
                <Button
                    onClick={() =>
                        accountImportModal.open({
                            onOk: () => {
                                accountImportModal.close();
                                reload();
                            },
                        })
                    }>
                    导入
                </Button>
                <Button
                    type="primary"
                    onClick={() =>
                        editModal.open({
                            onOk: () => {
                                editModal.close();
                                reload();
                            },
                        })
                    }
                    icon={<PlusOutlined />}>
                    新增
                </Button>
            </Space>
        </div>
    );

    const schema = {
        type: 'object',
        labelWidth: 100,
        properties: {
            keyword: {
                title: '关键字',
                type: 'string',
            },
            accountType: {
                title: '公众号类型',
                type: 'string',
                widget: 'SelectDict',
                props: {
                    options: dict.AccountType.data,
                },
            },
        },
    };

    const [deleteAction, {loading: deleteLoading}] =
        useRequestFn(deleteAccount);

    async function onDelete(id: string) {
        try {
            await deleteAction(id);
            await reload();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const columns: ProColumnsType<AccountList> = [
        {
            title: '公众号名称',
            dataIndex: 'nickname',
        },
        {
            title: '公众号类型',
            dataIndex: 'accountType',
            width: 150,
            render: dict.AccountType.getName,
        },
        {
            title: '公众号微信号码',
            dataIndex: 'wechatNumber',
        },
        {
            title: '说明',
            dataIndex: 'description',
            render: text => <OverText text={text} length={15} />,
        },
        {
            title: '创建时间',
            dataIndex: 'createDate',
            width: 160,
            render: (createTime: string) =>
                formatDate(createTime, 'YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '操作',
            fixed: 'right',
            dataIndex: 'id',
            width: 160,
            align: 'center',
            render: (id: string, item) => (
                <Space>
                    <Button
                        size="small"
                        type="link"
                        onClick={() => {
                            editModal.open({
                                data: item,
                                onOk: () => {
                                    editModal.close();
                                    reload();
                                },
                            });
                        }}>
                        修改
                    </Button>
                    <DeleteConfirm onSuccess={() => onDelete(id)}>
                        <Button size="small" type="link">
                            删除
                        </Button>
                    </DeleteConfirm>
                </Space>
            ),
        },
    ];

    return (
        <MainLayout.SpaceWrapper padding>
            <Spin spinning={deleteLoading}>
                <TableRender
                    rowKey="id"
                    ref={tableRef}
                    search={{schema, widgets: {SelectDict}, column: 4}}
                    columns={columns}
                    toolbarRender={toolBar}
                    scroll={{x: 'max-content'}}
                    {...tableInit()}
                />
            </Spin>

            {editModal.render()}
            {accountImportModal.render()}
        </MainLayout.SpaceWrapper>
    );
}
